import { clsx } from 'clsx';
import { HugeiconsIcon } from '@hugeicons/react';
import { ComputerIcon } from '@hugeicons/core-free-icons';
import { log } from '@/lib/logger';

export interface EnvironmentInfo {
  browser: { name: string; version: string };
  os: string;
  viewport: { width: number; height: number };
  screen: { width: number; height: number };
  devicePixelRatio: number;
}

interface EnvironmentSectionProps {
  environment: EnvironmentInfo;
}

/**
 * Collapsible environment information section for the issue sidebar.
 * Shows Browser, OS, Screen, Viewport, and Pixel Ratio in labeled rows.
 * Supports edit mode when onSave is provided.
 */
export function EnvironmentSection({ environment }: EnvironmentSectionProps) {
  log.info('[EnvironmentSection] Rendering:', { environment });

  const formatDimensions = (w: number, h: number) =>
    w && h ? `${w} \u00d7 ${h}` : 'Unknown';

  const formatDPR = (dpr: number) =>
    dpr ? `${dpr}x` : 'Unknown';

  const formatBrowser = (name: string, version: string) =>
    name !== 'Unknown' ? `${name} ${version}`.trim() : 'Unknown';

  const rows = [
    { label: 'Browser', value: formatBrowser(environment.browser.name, environment.browser.version) },
    { label: 'OS', value: environment.os || 'Unknown' },
    { label: 'Screen', value: formatDimensions(environment.screen.width, environment.screen.height) },
    { label: 'Viewport', value: formatDimensions(environment.viewport.width, environment.viewport.height) },
    { label: 'Pixel Ratio', value: formatDPR(environment.devicePixelRatio) },
  ];

  return (
    <section id="issue-environment" className="card-sm">
      <h2 className="section-label mb-3 flex items-center gap-2"><HugeiconsIcon icon={ComputerIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />Environment</h2>

      <dl id="environment-details" className="space-y-3 text-sm mt-4">
        {rows.map(({ label, value }) => (
          <div key={label}>
            <dt className="section-title mb-1">{label}</dt>
            <dd
              className={clsx(
                'font-medium',
                value === 'Unknown' ? 'text-gray-300' : 'text-gray-900'
              )}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
